import { createReducer, on } from '@ngrx/store';
import { apiResponse } from 'src/app/core/api/api.actions';
import { isApi } from 'src/app/core/api/api.utilities';
import { logout } from 'src/app/core/user/user.actions';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { clearOrdersAction, routeToOrdersAction } from '../orders/orders.actions';
import {
	clearRecoverStateAction,
	setAutoLoginRecoverCredentialsAction,
	setOrderSearchRecoverCredentialsAction,
	setRecoverMFATokenAction,
	updateCTokenAction,
	updateEmailAction,
	updateETokenAction,
	updateOrderIdAction,
	updateOTokenAction,
	updatePTokenAction,
	updateBarcodeAction
} from './recover.actions';
import { initialState, RecoverState } from './recover.state';

/**
 * Called when a user is trying to do an order search to recover their account.
 */
const onSetOrderSearchCredentials = on(
	setOrderSearchRecoverCredentialsAction,
	(state: RecoverState, action): RecoverState => {
		return {
			...state,
			...action.payload,
		};
	}
);

const onSetAutoLoginCredentials = on(
	setAutoLoginRecoverCredentialsAction,
	(state: RecoverState, action): RecoverState => {
		const { oToken, eToken, user_token, postVerifyAction } = action.payload;
		return {
			...state,
			oToken,
			eToken,
			user_token,
			postVerifyAction,
		};
	}
);

const onSetMFAToken = on(setRecoverMFATokenAction, (state: RecoverState, action): RecoverState => {
	const { mfaToken } = action;

	return {
		...state,
		mfa_token: mfaToken,
	};
});

const onClearRecover = on(clearRecoverStateAction, logout, (): RecoverState => ({}));

const onRouteToOrders = on(routeToOrdersAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		orders: [...action.payload],
	};
});

// Called when routing away from orders
const onClearOrders = on(clearOrdersAction, (state: RecoverState): RecoverState => {
	const newState = { ...state };
	delete newState.orders;
	return newState;
});

const onUpdateBillingInfoResponse = on(apiResponse, (state: RecoverState, action): RecoverState => {
	if (isApi(action, ApiRequestType.UPDATE_BILLING_INFO)) {
		if (!action.isOk) {
			return state;
		}

		if (action.response.eToken) {
			return {
				...state,
				eToken: action.response.eToken,
			};
		}
	}

	return state;
});

const onRefreshMfaCodeResponse = on(apiResponse, (state: RecoverState, action): RecoverState => {
	if (isApi(action, ApiRequestType.REFRESH_MFA)) {
		if (!action.isOk) {
			return state;
		}

		if (action.response.mfa_code) {
			return {
				...state,
				mfa_token: action.response.mfa_code,
			};
		}
	}
	return state;
});

const onUpdateETokenAction = on(updateETokenAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		eToken: action.eToken,
	};
});

const onUpdatePTokenAction = on(updatePTokenAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		pToken: action.pToken,
	};
});

const onUpdateCTokenAction = on(updateCTokenAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		cToken: action.cToken,
	};
});

const onUpdateOTokenAction = on(updateOTokenAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		oToken: action.oToken,
	};
});

const onUpdateOrderIdAction = on(updateOrderIdAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		order_id: action.order_id,
	};
});

const onUpdateEmailAction = on(updateEmailAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		email: action.email,
	};
});

const onUpdateBarcodeAction = on(updateBarcodeAction, (state: RecoverState, action): RecoverState => {
	return {
		...state,
		barcode: action.barcode,
	};
});

export const recoverReducer = createReducer(
	initialState,
	onSetOrderSearchCredentials,
	onSetAutoLoginCredentials,
	onClearRecover,
	onRouteToOrders,
	onSetMFAToken,
	onClearOrders,
	onUpdateBillingInfoResponse,
	onRefreshMfaCodeResponse,
	onUpdateOTokenAction,
	onUpdateETokenAction,
	onUpdatePTokenAction,
	onUpdateCTokenAction,
	onUpdateOrderIdAction,
	onUpdateEmailAction,
	onUpdateBarcodeAction
);
